/* Sign in to the Administration console.
 *
 * Deliberately NOT the member app's SignInPage, despite the identical card.
 * Three things differ and every one of them matters here:
 *
 *   - No "Sign up" link. Nobody self-registers as an administrator.
 *   - No "Forgot password" link. There is no self-service reset for an account
 *     that can delete a church; it goes through whoever runs the platform.
 *   - A member who signs in correctly is still turned away, with their session
 *     cleared — see below.
 *
 * Sharing the component and hiding those with props would put the member app
 * one boolean away from rendering an admin sign-in.
 */
import { useState, type FormEvent } from 'react'
import { AlertCircle } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { api, tokens, ApiError } from '@/api/client'
import type { Me, Session } from '@/types/api'

export function ConsoleSignInPage({ onSignedIn }: { onSignedIn: () => void }) {
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)

    try {
      const session = await api.post<Session>(
        '/auth/login',
        { identifier: identifier.trim(), password },
        { auth: false },
      )
      tokens.save(session)

      /* Correct password, wrong audience.
       *
       * SUPER ADMIN ONLY. Every console endpoint is permission-checked
       * server-side as well; this check is so the answer is a sentence
       * rather than a screen of 403s.
       *
       * ONE SENTENCE, AND IT NAMES NOTHING. A refusal that lists what is
       * behind the door — screens, roles, features — describes the tool to
       * someone who was just told they may not use it. It says only that
       * this sign-in is not theirs.
       *
       * The session is cleared on the way out: leaving a valid token in
       * localStorage on the admin origin serves no purpose. */
      const me = await api.get<Me>('/auth/me')
      if (me.role !== 'super_admin') {
        tokens.clear()
        setError('This sign-in is not available for your account.')
        setBusy(false)
        return
      }

      onSignedIn()
    } catch (err) {
      // A failure anywhere above may have left a token behind — a saved
      // session whose /auth/me never succeeded is worse than none.
      tokens.clear()
      setError(messageFor(err))
      setBusy(false)
    }
  }

  return (
    <div className="grid min-h-screen grid-cols-[minmax(0,1fr)] place-items-center bg-canvas p-s5">
      <div className="w-full max-w-[420px] rounded-md bg-surface p-s6 shadow-2">
        <div className="mb-s6 flex flex-col items-center gap-s3">
          <div className="grid h-[60px] w-[60px] place-items-center rounded-pill bg-brand">
            <svg viewBox="0 0 24 24" width="30" height="30" fill="none"
                 stroke="#fff" strokeWidth="2.4" strokeLinecap="round">
              <path d="M12 4v16M6.5 9.5h11" />
            </svg>
          </div>
          <div className="text-center">
            <div className="text-xl font-extrabold tracking-tight text-brand">
              Connect In God
            </div>
            <div className="text-base text-ink-2">Administration</div>
          </div>
        </div>

        <form onSubmit={submit} noValidate>
          {error ? (
            <div
              role="alert"
              className="mb-s4 flex items-start gap-s2 rounded border border-danger/30 bg-danger-soft p-s3 text-base text-danger"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>{error}</span>
            </div>
          ) : null}

          <Field
            label="Mobile or email"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            autoComplete="username"
            autoFocus
            required
          />

          <Field
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            reveal
            autoComplete="current-password"
            required
          />

          <Button
            variant="primary"
            size="lg"
            full
            type="submit"
            loading={busy}
            disabled={!identifier.trim() || !password}
            className="mt-s3"
          >
            Sign in
          </Button>
        </form>

      </div>
    </div>
  )
}

/** The same vagueness as the member app, for the same reason: naming which of
 *  the two was wrong would let anyone test whether a number is registered. */
function messageFor(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Could not sign you in.'
  if (err.status === 0) {
    return 'You appear to be offline. Check your connection and try again.'
  }
  if (err.status === 401 || err.code === 'invalid_credentials') {
    return 'Those details did not match. Check them and try again.'
  }
  return err.message || 'Could not sign you in.'
}
